// react
import { useCallback } from 'react';

// store
import {
  useCreateOrganiseEntryMutation,
  useUpdateOrganiseEntryMutation,
  useUpdateOrganiseEntryStatusMutation,
  useDeleteOrganiseEntryMutation,
  useCreateOrganiseSeriesMutation,
  useUpdateOrganiseSeriesMutation,
  useDeleteOrganiseSeriesMutation,
  useCreateOrganiseExceptionMutation,
} from 'store/api/organise';

// hooks
import { useTracking, TrackingEvent } from 'hooks/useTracking';

// types
import {
  OrganiseEntry,
  OrganiseEntryStatus,
  RecurrenceFrequency,
} from '@karehero/models';
import { EditEntryState } from '@karehero/llama/pwa/organisms/EntryDialog/EditEntryDialog';
import { EntryState } from '@karehero/llama/pwa/organisms/OrganiserEntryForm/OrganiserEntryForm';

const RRULE_FREQ_MAP: Record<RecurrenceFrequency, string> = {
  daily: 'DAILY',
  weekly: 'WEEKLY',
  monthly: 'MONTHLY',
};

const ISOTime = (ts: string | null) =>
  ts ? new Date(ts).toISOString().slice(11, 19) : null;

const buildRRule = (
  frequency: RecurrenceFrequency,
  interval: number,
): string => {
  let rrule = `FREQ=${RRULE_FREQ_MAP[frequency]}`;
  if (interval > 1) {
    rrule += `;INTERVAL=${interval}`;
  }
  return rrule;
};

export const useOrganiserActionsWithTracking = () => {
  // Tracking
  const { track } = useTracking();

  // Mutations
  const [createEntry, { isLoading: isCreating }] =
    useCreateOrganiseEntryMutation();
  const [updateEntry, { isLoading: isUpdating }] =
    useUpdateOrganiseEntryMutation();
  const [createRepeatingTask, { isLoading: isCreatingRepeatingTask }] =
    useCreateOrganiseSeriesMutation();
  const [updateRepeatingTask] = useUpdateOrganiseSeriesMutation();
  const [updateStatus] = useUpdateOrganiseEntryStatusMutation();
  const [deleteEntry] = useDeleteOrganiseEntryMutation();
  const [createException] = useCreateOrganiseExceptionMutation();
  const [deleteRepeatingTask] = useDeleteOrganiseSeriesMutation();

  const handleUpdate = useCallback(
    async (entry: EditEntryState) => {
      if (entry.isUpdatingRepeatingTask && entry.recurringEventId) {
        await updateRepeatingTask({
          calendarId: entry.calendarId,
          seriesId: entry.recurringEventId,
          body: {
            title: entry.title.trim(),
            rrule: buildRRule(
              entry.recurrenceFrequency,
              entry.recurrenceInterval,
            ),
            defaultAssigneeAccountId: entry.assigneeAccountId,
            startTime: ISOTime(entry.startTime),
            endTime: ISOTime(entry.endTime),
          },
        });
        track(TrackingEvent.OrganiserSeriesEdited, {
          careCircleId: entry.calendarId,
        });
        return;
      }

      if (entry.entryId) {
        await updateEntry({
          calendarId: entry.calendarId,
          entryId: entry.entryId,
          body: {
            title: entry.title.trim(),
            assigneeAccountId: entry.assigneeAccountId,
            startTime: entry.startTime,
            endTime: entry.endTime,
          },
        });
        track(TrackingEvent.OrganiserEntryEdited, {
          careCircleId: entry.calendarId,
        });
        return;
      }
    },
    [updateEntry, updateRepeatingTask, track],
  );

  // Returns the created entry/series (or undefined on error) so callers can act
  // on the new id — e.g. linking uploaded documents to the new entry.
  const handleCreate = useCallback(
    async (entry: EntryState) => {
      if (entry.isRecurring) {
        const res = await createRepeatingTask({
          calendarId: entry.calendarId,
          body: {
            title: entry.title.trim(),
            rrule: buildRRule(
              entry.recurrenceFrequency,
              entry.recurrenceInterval,
            ),
            defaultAssigneeAccountId: entry.assigneeAccountId,
            startTime: entry.startTime
              ? new Date(entry.startTime).toISOString().slice(11, 19)
              : null,
            endTime: entry.endTime
              ? new Date(entry.endTime).toISOString().slice(11, 19)
              : null,
          },
        });
        track(TrackingEvent.OrganiserSeriesCreated, {
          frequency: entry.recurrenceFrequency,
          interval: entry.recurrenceInterval,
          hasAssignee: !!entry.assigneeAccountId,
          careCircleId: entry.calendarId,
        });
        return 'data' in res ? res.data : undefined;
      }

      const res = await createEntry({
        calendarId: entry.calendarId,
        body: {
          title: entry.title,
          assigneeAccountId: entry.assigneeAccountId,
          startTime: entry.startTime,
          endTime: entry.endTime,
        },
      });
      track(TrackingEvent.OrganiserEntryCreated, {
        hasAssignee: !!entry.assigneeAccountId,
        hasDate: !!entry.startTime,
        careCircleId: entry.calendarId,
      });
      return 'data' in res ? res.data : undefined;
    },
    [createEntry, createRepeatingTask, track],
  );

  const handleDelete = useCallback(
    async (calendarId: string, entryId: string) => {
      await deleteEntry({
        calendarId: calendarId,
        entryId: entryId,
      });

      track(TrackingEvent.OrganiserEntryDeleted, {
        careCircleId: calendarId,
      });
    },
    [deleteEntry, track],
  );

  const handleCancelOccurrence = useCallback(
    async (
      calendarId: string,
      recurringEventId: string,
      occurrenceDate: string,
    ) => {
      await createException({
        calendarId: calendarId,
        seriesId: recurringEventId,
        body: { date: occurrenceDate },
      });

      track(TrackingEvent.OrganiserExceptionCreated, {
        careCircleId: calendarId,
      });
    },
    [createException, track],
  );

  const handleDeleteRepeatingTask = useCallback(
    async (calendarId: string, recurringEventId: string) => {
      await deleteRepeatingTask({
        calendarId: calendarId,
        seriesId: recurringEventId,
      });

      track(TrackingEvent.OrganiserSeriesDeleted, {
        careCircleId: calendarId,
      });
    },
    [deleteRepeatingTask, track],
  );

  const trackStatusChange = useCallback(
    (entry: OrganiseEntry, newStatus: OrganiseEntryStatus) => {
      const calendarId = entry.calendarId;
      const isRecurring = !!entry?.recurringEventId;
      const eventType = {
        [OrganiseEntryStatus.Completed]: TrackingEvent.OrganiserEntryCompleted,
        [OrganiseEntryStatus.Open]: TrackingEvent.OrganiserEntryReopened,
        [OrganiseEntryStatus.WontDo]: TrackingEvent.OrganiserEntryMarkedWontDo,
      }[newStatus];

      track(eventType, {
        isRecurring,
        careCircleId: calendarId,
      });
    },
    [track],
  );

  const handleStatusChange = useCallback(
    async (entry: OrganiseEntry, status: OrganiseEntryStatus) => {
      await updateStatus({
        calendarId: entry.calendarId,
        entryId: entry.id,
        body: { status },
      });

      trackStatusChange(entry, status);
    },
    [updateStatus, trackStatusChange],
  );

  return {
    isPerformingAction: isCreating || isUpdating || isCreatingRepeatingTask,
    createEntry: handleCreate,
    updateEntry: handleUpdate,
    deleteEntry: handleDelete,
    cancelOccurence: handleCancelOccurrence,
    deleteRepeatingTask: handleDeleteRepeatingTask,
    changeStatus: handleStatusChange,
  };
};
